import { notification } from "antd";
import Api from "./ApiHandler";
import { API_URL } from "./Paths";
import { isKnownTopLevelDomain } from "./TopLevelDomains";

export const isEqualsArray = (arr1, arr2) => {
	if (!(arr1 instanceof Array) || !(arr1 instanceof Array)) return false;

	if (arr1.length !== arr2.length) return false;

	if (arr1 instanceof Object && arr2 instanceof Object) {
		let isEqual = true,
			arrayLength = arr1.length;

		for (let index = 0; index < arrayLength && isEqual; index++) {
			if (JSON.stringify(arr1[index]) !== JSON.stringify(arr2[index])) isEqual = false;
		}

		return isEqual;
	}

	return false;
};

export const isEmpty = (obj) => {
	for (var key in obj) {
		if (obj.hasOwnProperty(key)) return false;
	}

	return true;
};

export const isIEBrowser = () => {
	const ua = window.navigator.userAgent;

	const msie = ua.indexOf("MSIE ");
	const msie11 = ua.indexOf("Trident/");

	return msie > 0 || msie11 > 0;
};

export const getBase64 = (img, callback) => {
	const reader = new FileReader();

	reader.addEventListener("load", () => callback(reader.result));
	reader.readAsDataURL(img);
};

export const beforeFileUpload = (file) => {
	const isLt2M = file.size / 1024 / 1024 < 2;
	const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";

	if (!isJpgOrPng) notification.error({ message: "Error", description: "You can only upload JPG/PNG file!", duration: 3 });

	if (!isLt2M) notification.error({ message: "Error", description: "Image must smaller than 2MB!", duration: 3 });

	return isJpgOrPng && isLt2M;
};

export const uploadMedia = async (mediaType, imageState) => {
	try {
		let fileName = undefined;
		const formData = new FormData();

		formData.append("file", imageState.file);

		let fileResponse = await new Api().post(`${API_URL.UPLOAD_MEDIA}`, { params: { mediaType: mediaType.toUpperCase() }, data: formData });

		fileName = fileResponse.data.fileName;

		return fileName;
	} catch (error) {
		console.log("🚀 ~ file: Utils.js ~ line 78 ~ uploadMedia ~ error", error);
	}
};

export const importAll = (r) => r.keys().map(r);

/**
 * antd's built-in `type: "email"` rule only checks the shape of the address, not that the domain
 * could exist: "admin@admin", "admin@admin.c", "admin@-admin.com" and "admin@admin..com" all pass
 * it, and each one was accepted by Add User. A CMS user is created with an address the platform is
 * expected to send mail to, so the domain has to be a real one - labels that do not start or end
 * with a hyphen, no empty label, and an alphabetic top level domain of at least two characters.
 * The local part is kept deliberately permissive; only leading, trailing and doubled dots are
 * rejected there.
 */
export const EMAIL_ADDRESS_PATTERN = /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/**
 * Shape alone is not enough. "sepozoxymo@mailinator.inn" satisfies every rule above and was
 * accepted, because ".inn" looks exactly like ".inc" to a pattern - it is simply not a domain that
 * exists. The last label is therefore checked against the list IANA actually delegates, which is
 * what catches the typo endings (.inn, .cmo, .con, .comm) that reach this form in practice.
 *
 * What this still cannot know is whether the domain itself is real: "someone@notarealcompany.com"
 * passes, because only a DNS lookup answers that, and that belongs on the server.
 */
export const isValidEmailAddress = (value) => {
	const address = String(value ?? "").trim();

	if (!EMAIL_ADDRESS_PATTERN.test(address)) return false;

	const domain = address.slice(address.lastIndexOf("@") + 1);

	return isKnownTopLevelDomain(domain.slice(domain.lastIndexOf(".") + 1));
};

/**
 * Form rule for an email field. Pair it with `{ required: true }` - an empty value is left to that
 * rule so the admin does not get two messages for one empty field. The value is trimmed before the
 * test, and the forms using this rule trim before they submit, so a pasted address with a trailing
 * space is accepted and stored clean rather than saved with the space in it.
 */
export const emailAddressRule = (message = "Please enter valid email address.") => ({
	validator: (_, value) => {
		if (value === undefined || value === null || String(value).trim() === "") return Promise.resolve();

		return isValidEmailAddress(value) ? Promise.resolve() : Promise.reject(new Error(message));
	},
});

/**
 * Form rule for a password field. A password made of, or padded with, spaces is almost always a
 * typo, and it is one the admin cannot see in a masked field: the account is then created with a
 * password nobody can reproduce at the login screen. Any whitespace anywhere in the value -
 * leading, trailing or in the middle, tabs included - is rejected here rather than silently saved.
 * This rule deliberately adds nothing else: length and complexity are unchanged.
 */
export const noWhitespacePasswordRule = (message = "Password cannot contain spaces.") => ({
	validator: (_, value) => {
		if (value === undefined || value === null || value === "") return Promise.resolve();

		return /\s/.test(String(value)) ? Promise.reject(new Error(message)) : Promise.resolve();
	},
});
